import React, { useState, useEffect } from 'react';
import { FileText, Save, CheckCircle, Loader2, Clock } from 'lucide-react';
import { useCoverLetter } from '../../hooks/useCoverLetter';

export default function CoverLetterManager() {
  const { activeCoverLetter, loading, error: hookError, saveOrUpdate } = useCoverLetter();
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (activeCoverLetter) {
      setContent(activeCoverLetter.content);
    }
  }, [activeCoverLetter]);

  useEffect(() => {
    if (activeCoverLetter) {
      setHasChanges(content !== activeCoverLetter.content);
    } else {
      setHasChanges(content.trim().length > 0);
    }
  }, [content, activeCoverLetter]);

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Cover letter cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const success = await saveOrUpdate(content.trim());
      if (success) {
        setSaved(true);
        setHasChanges(false);
        setTimeout(() => setSaved(false), 4000);
      }
    } catch {
      setError('Failed to save cover letter');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-5 w-5 text-green-600" />
          <h3 className="font-semibold text-green-900">Your Saved Cover Letter</h3>
        </div>
        <p className="text-sm text-green-800 leading-relaxed">
          Write your cover letter here once, and it will automatically fill in every time you apply for a job.
          You can always customize it per application.
        </p>
      </div>

      {(error || hookError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          {error || hookError}
        </div>
      )}

      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          Cover letter saved. It will auto-fill on your next job application.
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Cover Letter Editor</span>
          </div>
          <span className="text-xs text-gray-400">{content.length} characters</span>
        </div>

        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Dear Hiring Manager,

I am writing to express my strong interest in this position. With my experience in...

Highlight your skills, experience, and what makes you a great fit for the types of jobs you are applying to. This letter will be used as a template for all your applications."
          className="w-full px-5 py-4 text-sm leading-relaxed resize-none focus:outline-none min-h-[280px]"
          rows={12}
        />

        <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            {activeCoverLetter && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Last saved {new Date(activeCoverLetter.updated_at).toLocaleDateString()} at{' '}
                {new Date(activeCoverLetter.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {hasChanges && (
              <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
            )}
          </div>

          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Cover Letter
          </button>
        </div>
      </div>
    </div>
  );
}
